// src/components/SpecialOffers/VendorCard.js

import CalculateIcon from "@mui/icons-material/Calculate";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Alert,
  Box,
  Button,
  Card,
  CardMedia,
  Grid,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  callbackCooldownDays,
  processHtmlContent
} from "../../../utils/utils";
import LiquidGlass from "../../LiquidGlass";

const VendorCard = ({
  vendor,
  columnCount,
  callbackRequests,
  expandedVendors,
  category,
  handleToggleDescription,
  handleOpenModal,
  handleOpenQuickQuote,
  handleWhatsAppClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isExpanded = expandedVendors[vendor.id];
  const rawDesc = vendor.description;
  const description = isMobile
    ? rawDesc
    : isExpanded
    ? rawDesc
    : rawDesc.length > 400
    ? `${rawDesc.slice(0, 400)}...`
    : rawDesc;

  const callbackData = callbackRequests[vendor.id];
  const quoteRequested = Boolean(callbackData);
  const quoteRequestId = callbackData?.id || null;

  const isCallbackAllowed = (id) => {
    const data = callbackRequests[id];
    if (!data) return true;
    const days = Math.floor(
      (new Date() - data.createdAt) / (1000 * 60 * 60 * 24)
    );
    return days >= callbackCooldownDays;
  };

  const buttons = [
    isCallbackAllowed(vendor.id) && {
      label: "Call Me",
      icon: <PhoneIcon />,
      onClick: () => handleOpenModal(vendor),
    },
    isCallbackAllowed(vendor.id) && vendor.quoteForm && {
      label: "Quick Quote",
      icon: <CalculateIcon />,
      onClick: () => handleOpenQuickQuote(vendor),
    },
    {
      label: "Chat To Us",
      icon: <WhatsAppIcon />,
      onClick: () =>
        handleWhatsAppClick(vendor, quoteRequested, quoteRequestId),
    },
  ].filter(Boolean);

  // If vendor.whatsAppActive is false, remove the WhatsApp button
  if (!vendor.whatsAppActive) {
    buttons.pop();
  }

  return (
    <Grid item xs={12} sm={12 / columnCount} md={12 / columnCount}>
      <LiquidGlass
        altComponent={Card}
        altSx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "rgba(0,0,0,0.08)",
          bgcolor: "#ffffff",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          "&:hover": {
            boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
            transform: "translateY(-2px)",
            borderColor: "rgba(0,0,0,0.12)",
          },
          ...(isMobile && {
            bgcolor: "transparent",
            border: "none",
            boxShadow: "none",
            "&:hover": {
              transform: "none",
              boxShadow: "none",
            },
          }),
        }}
      >
        <CardMedia
          component="img"
          image={vendor.headerImageUrl}
          alt={`${vendor.name} logo`}
          sx={{
            height: { xs: 120, sm: 130, md: 140 },
            width: "100%",
            objectFit: "contain",
            objectPosition: `${vendor.headerImagePosition || "center"}`,
            borderRadius: "12px 12px 0 0",
            backgroundColor: `${vendor.headerBackgroundColor || "#ffffff"}`,
            backgroundPosition: `${vendor.headerImagePosition || "center"}`,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        />

        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography
              variant="body2"
              dangerouslySetInnerHTML={{
                __html: processHtmlContent(description),
              }}
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                lineHeight: 1.4,
                mb: 1,
                "& sup": {
                  fontSize: "0.6em",
                  verticalAlign: "super",
                },
                "& sub": {
                  fontSize: "0.6em",
                  verticalAlign: "sub",
                },
                "& h1, & h2, & h3, & h4, & h5, & h6": {
                  margin: "8px 0 4px 0",
                  fontWeight: "bold",
                },
                "& h1": { fontSize: "1.2em" },
                "& h2": { fontSize: "1.1em" },
                "& h3": { fontSize: "1.05em" },
                "& h4, & h5, & h6": { fontSize: "1em" },
                ...(isMobile && {
                  color: "#fff",
                  textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
                }),
              }}
            />

            { !isMobile && !isExpanded && rawDesc.length > 400 && (
              <Link
                component="button"
                variant="body2"
                onClick={() => handleToggleDescription(vendor.id)}
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.8rem" },
                  color: isMobile ? "#fff" : "primary.main",
                  textDecoration: "underline",
                  mb: 1,
                }}
              >
                Read More
              </Link>
            )}

            {!isCallbackAllowed(vendor.id) && (
              <Alert
                severity="info"
                icon={<PhoneIcon />}
                sx={{
                  mb: 1,
                  fontSize: { xs: "0.75rem", sm: "0.8rem" },
                  py: 0.5,
                  ...(isMobile && {
                    backgroundColor: "rgba(255,255,255,0.2)",
                    backdropFilter: "blur(10px)",
                    color: "#fff",
                    textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
                    "& .MuiAlert-icon": { color: "#fff" },
                  }),
                }}
              >
                You've recently requested a callback from{" "}
                <strong>{vendor.name}</strong> regarding{" "}
                <strong>{category.replace(/-/g, " ")}</strong>. Contact the Finatic.co.za support team <a href="https://wapp.to/finatic" target="_blank">directly on WhatsApp</a> if needed.
              </Alert>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              mt: "auto",
              bgcolor: isMobile ? "transparent" : "rgba(248,250,252,0.8)",
              borderTop: "1px solid",
              borderColor: isMobile ? "transparent" : "rgba(0,0,0,0.06)",
            }}
          >
            {buttons.map((button, idx) => (
              <Button
                key={button.label}
                variant="contained"
                onClick={button.onClick}
                startIcon={button.icon}
                sx={{
                  flex: isMobile ? "none" : 1,
                  width: isMobile ? "100%" : "auto",
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  py: { xs: 1, sm: 1 },
                  px: { xs: 0.5, sm: 1 },
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  borderRadius: 0,
                  ...(idx === 0 && {
                    borderTopLeftRadius: isMobile ? 0 : 0,
                    borderBottomLeftRadius: isMobile ? 0 : 6,
                  }),
                  ...(idx === buttons.length - 1 && {
                    borderBottomRightRadius: isMobile ? 6 : 0,
                    borderTopRightRadius: 0,
                    borderBottomLeftRadius: isMobile ? 6 : 0,
                  }),
                  borderTop:
                    isMobile && idx !== 0
                      ? "1px solid rgba(0,0,0,0.12)"
                      : "none",
                  borderRight:
                    !isMobile && idx !== buttons.length - 1
                      ? "1px solid rgba(0,0,0,0.12)"
                      : "none",
                  textTransform: "none",
                  fontWeight: 500,
                  color: isMobile ? "#64748b" : "#64748b",
                  bgcolor: isMobile ? "#ffffff" : "transparent",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "primary.main",
                    color: "#fff",
                    "& .MuiSvgIcon-root": {
                      color: "#fff",
                    },
                    boxShadow: "none",
                  },
                }}
              >
                {button.label}
              </Button>
            ))}
          </Box>
        </Box>
      </LiquidGlass>
    </Grid>
  );
};

export default VendorCard;
